import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { Button, Form, Checkbox, Radio } from 'tea-component';
import TeaFormItem from '@gp/components/TeaFormItem';
import { FILE_TYPES_MAP } from '@gp/constants/const';
import { PROJECTS_MAP, PROJECT_DETAIL_MAP, LATEST_GR_MAP, STAKEHOLDER_MAP, TECHNICAL_MAP } from './constants';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface SelectAllResultType {
  projects: string[] | undefined;
  projectDetail: string[] | undefined;
  latestGR: string[] | undefined;
  stakeholder: string[] | undefined;
  tech: string[] | undefined;
}

interface ResultType extends SelectAllResultType {
  // 1: CSV 2: EXCEL
  fileType: '1' | '2' | (string & {}) | undefined;
}

interface IDownloadBoxProps extends IBaseProps {
  isUpdatesPermission: boolean;
  isPPSPermission: boolean;
  onChange?: (payload: ResultType) => void;
}

const PROJECT_DETAIL_VALUES = PROJECT_DETAIL_MAP.map((item) => item?.value);
const LATEST_GR_VALUES = LATEST_GR_MAP.map((item) => item?.value);
const STAKEHOLDER_VALUES = STAKEHOLDER_MAP.map((item) => item?.value);
const TECHNICAL_VALUES = TECHNICAL_MAP.map((item) => item?.value);

const CheckAll = (props: {
  field: string;
  allValues: string[];
  result: any;
  setResult: React.Dispatch<React.SetStateAction<any>>;
  checked: string[] | undefined;
  setChecked: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  onChange?: (payload: any) => void;
}) => {
  const { field, allValues, result, setResult, checked, setChecked, onChange } = props || {};
  return (
    <Checkbox
      indeterminate={!!checked?.length && checked?.length !== allValues.length}
      value={!!checked?.length && checked?.length === allValues.length}
      onChange={(payload) => {
        const valueTmp = !payload ? [] : allValues;
        setChecked(valueTmp);
        const newResult = {
          ...result,
          [field]: valueTmp,
        };
        onChange?.(newResult);
        setResult(newResult);
      }}
    >
      All
    </Checkbox>
  );
};

const DownloadBox: React.FC<IDownloadBoxProps> = (props) => {
  const { className, style, isUpdatesPermission = false, isPPSPermission = false, onChange } = props;

  // 暂存选择的结果
  const [result, setResult] = useState<ResultType>({
    projects: undefined,
    projectDetail: undefined,
    latestGR: undefined,
    stakeholder: undefined,
    tech: undefined,
    fileType: undefined,
  });
  // 全选要选中的项
  const [selectAllResult, setSelectAllResult] = useState<SelectAllResultType>({
    projects: undefined,
    projectDetail: undefined,
    latestGR: undefined,
    stakeholder: undefined,
    tech: undefined,
  });
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [projects, setProjects] = useState<string[] | undefined>(undefined);
  const [projectDetail, setProjectDetail] = useState<string[] | undefined>(undefined);
  const [latestGR, setLatestGR] = useState<string[] | undefined>(undefined);
  const [stakeholder, setStakeholder] = useState<string[] | undefined>(undefined);
  const [tech, setTech] = useState<string[] | undefined>(undefined);
  const [fileType, setFileType] = useState<string | undefined>(undefined);

  // 根据权限组织默认数据
  const projectsOptions = useMemo(() => {
    const options = PROJECTS_MAP;
    // 放开所有下载字段权限
    // 去除没有权限的选项
    // if (!isUpdatesPermission) {
    //   options = options?.filter((t) => t?.text !== 'Project Updates');
    // }
    // if (!isPPSPermission) {
    //   options = options?.filter((t) => t?.text !== 'Status' && t?.text !== 'DPS Comment');
    // }
    // 默认全选All Projects的选项
    const defaultProjects = options?.map((t) => t?.value);
    const defaultFileType = FILE_TYPES_MAP.CSV;
    setProjects(defaultProjects);
    // 默认选择第一个文件类型
    setFileType(defaultFileType);
    const result = {
      projects: defaultProjects,
      projectDetail: undefined,
      latestGR: undefined,
      stakeholder: undefined,
      tech: undefined,
      fileType: defaultFileType,
    };
    onChange?.(result);
    setResult(result);
    // 组织Select All时选择哪些项
    const selectAllResult = {
      projects: defaultProjects,
      projectDetail: PROJECT_DETAIL_VALUES,
      latestGR: LATEST_GR_VALUES,
      stakeholder: STAKEHOLDER_VALUES,
      tech: TECHNICAL_VALUES,
    };
    setSelectAllResult(selectAllResult);

    return options;
  }, [isUpdatesPermission, isPPSPermission]);

  // 切换全选及清空
  const triggerSelectAll = (isSelectAll: boolean) => {
    if (isSelectAll) {
      const newResult = {
        projects: selectAllResult?.projects,
        projectDetail: selectAllResult?.projectDetail,
        latestGR: selectAllResult?.latestGR,
        stakeholder: selectAllResult?.stakeholder,
        tech: selectAllResult?.tech,
        fileType: result?.fileType,
      };
      onChange?.(newResult);
      setResult(newResult);
      setProjects(selectAllResult?.projects);
      setProjectDetail(selectAllResult?.projectDetail);
      setLatestGR(selectAllResult?.latestGR);
      setStakeholder(selectAllResult?.stakeholder);
      setTech(selectAllResult?.tech);
    } else {
      const newResult = {
        projects: undefined,
        projectDetail: undefined,
        latestGR: undefined,
        stakeholder: undefined,
        tech: undefined,
        fileType: result?.fileType,
      };
      onChange?.(newResult);
      setResult(newResult);
      setProjects(undefined);
      setProjectDetail(undefined);
      setLatestGR(undefined);
      setStakeholder(undefined);
      setTech(undefined);
    }
  };

  return (
    <div style={style} className={classnames('dbg-project-list-download-modal-container', className)}>
      <Button
        className="dbg-project-list-download-modal-select-all-btn"
        type="link"
        onClick={() => {
          setSelectAll(!selectAll);
          triggerSelectAll(!selectAll);
        }}
      >
        {selectAll ? 'Clear All' : 'Select All'}
      </Button>
      <Form className="teaui-form-alignment dbg-download-form">
        {/* All Projects */}
        <TeaFormItem labelName="All Projects" className="form-item--all-project">
          <Checkbox.Group
            value={projects || []}
            onChange={(value) => {
              setProjects(value);
              const newResult = {
                ...result,
                projects: value,
              };
              onChange?.(newResult);
              setResult(newResult);
            }}
          >
            {projectsOptions?.map((t: { value: string; text: string }) => {
              const { value, text } = t || {};
              return (
                <Checkbox key={value} name={value}>
                  {text}
                </Checkbox>
              );
            })}
          </Checkbox.Group>
        </TeaFormItem>
        {/* More Info */}
        <TeaFormItem labelName="More Info" className="form-item--subform">
          <Form className="dbg-project-list-download-modal-gray-box" layout="fixed" fixedLabelWidth={170}>
            {/* Project Detail */}
            <TeaFormItem labelName="Overview">
              <Checkbox.Group
                value={projectDetail || []}
                onChange={(value) => {
                  setProjectDetail(value);
                  const newResult = {
                    ...result,
                    projectDetail: value,
                  };
                  onChange?.(newResult);
                  setResult(newResult);
                }}
              >
                <CheckAll
                  key="all"
                  field="projectDetail"
                  allValues={PROJECT_DETAIL_VALUES}
                  result={result}
                  setResult={setResult}
                  checked={projectDetail}
                  setChecked={setProjectDetail}
                  onChange={onChange}
                />
                {PROJECT_DETAIL_MAP?.map((t: { value: string; text: string }) => {
                  const { value, text } = t || {};
                  return (
                    <Checkbox key={value} name={value}>
                      {text}
                    </Checkbox>
                  );
                })}
              </Checkbox.Group>
            </TeaFormItem>
            {/* Latest GR */}
            <TeaFormItem labelName="Latest GR">
              <Checkbox.Group
                value={latestGR || []}
                onChange={(value) => {
                  setLatestGR(value);
                  const newResult = {
                    ...result,
                    latestGR: value,
                  };
                  onChange?.(newResult);
                  setResult(newResult);
                }}
              >
                <CheckAll
                  key="all"
                  field="latestGR"
                  allValues={LATEST_GR_VALUES}
                  result={result}
                  setResult={setResult}
                  checked={latestGR}
                  setChecked={setLatestGR}
                  onChange={onChange}
                />
                {LATEST_GR_MAP?.map((t: { value: string; text: string }) => {
                  const { value, text } = t || {};
                  return (
                    <Checkbox key={value} name={value}>
                      {text}
                    </Checkbox>
                  );
                })}
              </Checkbox.Group>
            </TeaFormItem>
            {/* Stakeholder */}
            <TeaFormItem labelName="Stakeholder" className="dbg-form-item--stakeholder">
              <Checkbox.Group
                value={stakeholder || []}
                onChange={(value) => {
                  setStakeholder(value);
                  const newResult = {
                    ...result,
                    stakeholder: value,
                  };
                  onChange?.(newResult);
                  setResult(newResult);
                }}
              >
                <CheckAll
                  key="all"
                  field="stakeholder"
                  allValues={STAKEHOLDER_VALUES}
                  result={result}
                  setResult={setResult}
                  checked={stakeholder}
                  setChecked={setStakeholder}
                  onChange={onChange}
                />
                {STAKEHOLDER_MAP?.map((t: { value: string; text: string }) => {
                  const { value, text } = t || {};
                  return (
                    <Checkbox key={value} name={value}>
                      {text}
                    </Checkbox>
                  );
                })}
              </Checkbox.Group>
            </TeaFormItem>
            {/* Technical */}
            <TeaFormItem labelName="Tech"  className="dbg-form-item--tech">
              <Checkbox.Group
                value={tech || []}
                onChange={(value) => {
                  setTech(value);
                  const newResult = {
                    ...result,
                    tech: value,
                  };
                  onChange?.(newResult);
                  setResult(newResult);
                }}
              >
                <CheckAll
                  key="all"
                  field="tech"
                  allValues={TECHNICAL_VALUES}
                  result={result}
                  setResult={setResult}
                  checked={tech}
                  setChecked={setTech}
                  onChange={onChange}
                />
                {TECHNICAL_MAP?.map((t: { value: string; text: string }) => {
                  const { value, text } = t || {};
                  return (
                    <Checkbox key={value} name={value}>
                      {text}
                    </Checkbox>
                  );
                })}
              </Checkbox.Group>
            </TeaFormItem>
          </Form>
        </TeaFormItem>
        {/* File Format */}
        <TeaFormItem labelName="File Format" className="dbg-form-item--file-format">
          <Radio.Group
            className="dbg-common-radio-group"
            value={fileType || ''}
            onChange={(value) => {
              setFileType(value);
              const newResult = {
                ...result,
                fileType: value,
              };
              onChange?.(newResult);
              setResult(newResult);
            }}
          >
            <Radio className="dbg-common-radio" name={FILE_TYPES_MAP.EXCEL}>
              Excel
            </Radio>
            <Radio className="dbg-common-radio" name={FILE_TYPES_MAP.CSV}>
              CSV
            </Radio>
          </Radio.Group>
        </TeaFormItem>
      </Form>
    </div>
  );
};

export default React.memo(DownloadBox);
